// src/pages/client/HelpAndSupportPage.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  ArrowLeft,
  Search,
  X,
  Phone,
  MessageCircle,
  Mail,
  Send,
  ChevronDown,
  ChevronUp,
  HelpCircle,
  User,
  Star,
  Heart,
  Flag,
  Shield,
  Wrench,
  AlertCircle,
  Loader2,
  CheckCircle,
  FileText,
  Building2
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import useTranslation from '../../hooks/useTranslation';
import colors from '../../constants/colors';
import styles from '../../styles/HelpAndSupportPage.module.css';

// Redux imports - Message
import { createMessage, resetMessageState, clearMessageErrors, clearMessageSuccess } from '../../store/slices/messageSlice';
import {
  selectSubmitMessageSuccess,
  selectSubmitMessageError,
  selectMessageSubmitting,
  selectCurrentMessage
} from '../../store/slices/messageSlice';

// Redux imports - Platform Settings
import { getPublicSettings } from '../../store/slices/platformSettingsSlice';
import {
  selectPublicSettings,
  selectSettingsLoading,
  selectSettingsError
} from '../../store/slices/platformSettingsSlice';

// ======================================================
// DUMMY DATA FOR PROVIDER SEARCH
// ======================================================

const dummyProviders = [
  { id: '1', name: 'JP Electrical Services', serviceType: 'Electrician' },
  { id: '2', name: 'MJ Plumbing Solutions', serviceType: 'Plumber' },
  { id: '3', name: 'Duval Construction', serviceType: 'Mason' },
  { id: '4', name: 'CPainting Services', serviceType: 'Painter' },
  { id: '5', name: 'MC Woodworks', serviceType: 'Carpenter' },
  { id: '6', name: "Isabelle's Barber Shop", serviceType: 'Barber' },
  { id: '7', name: 'CoolHaiti AC Services', serviceType: 'HVAC Technician' },
  { id: '8', name: 'CleanHaiti Services', serviceType: 'Cleaner' },
  { id: '9', name: 'Baptiste Plumbing', serviceType: 'Plumber' },
  { id: '10', name: 'Haitian Elegance Design', serviceType: 'Interior Designer' },
];

const HelpAndSupportPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { currentLanguage } = useLanguage();
  
  const [activeSection, setActiveSection] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Redux state - Message
  const submitSuccess = useSelector(selectSubmitMessageSuccess);
  const submitError = useSelector(selectSubmitMessageError);
  const isSubmitting = useSelector(selectMessageSubmitting);
  const currentMessage = useSelector(selectCurrentMessage);
  
  // Redux state - Platform Settings
  const publicSettings = useSelector(selectPublicSettings);
  const settingsLoading = useSelector(selectSettingsLoading);
  const settingsError = useSelector(selectSettingsError);
  
  // Message form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    content: '',
    messageType: 'general',
    preferredContact: 'email',
    preferredTime: 'anytime',
    serviceProviderId: '',
    serviceProviderName: '',
    serviceType: '',
    category: 'general_inquiry'
  });
  
  const [formErrors, setFormErrors] = useState({});
  const [submittedMessageId, setSubmittedMessageId] = useState(null);
  const [showProviderSearch, setShowProviderSearch] = useState(false);
  const [providerSearchTerm, setProviderSearchTerm] = useState('');
  const [selectedProvider, setSelectedProvider] = useState(null);

  // ======================================================
  // FETCH PLATFORM SETTINGS ON MOUNT
  // ======================================================
  useEffect(() => {
    dispatch(getPublicSettings());
    
    // Cleanup on unmount
    return () => {
      dispatch(resetMessageState());
      dispatch(clearMessageErrors());
      dispatch(clearMessageSuccess());
    };
  }, [dispatch]);

  // ======================================================
  // TRANSLATIONS
  // ======================================================
  const { translatedText: helpSupport } = useTranslation('Help & Support');
  const { translatedText: howCanWeHelp } = useTranslation('How can we help you?');
  const { translatedText: heroSubtitle } = useTranslation('Find answers to common questions or contact our support team');
  const { translatedText: searchPlaceholder } = useTranslation('Search for help topics...');
  const { translatedText: quickContact } = useTranslation('Quick Contact');
  const { translatedText: callUs } = useTranslation('Call Us');
  const { translatedText: whatsapp } = useTranslation('WhatsApp');
  const { translatedText: email } = useTranslation('Email');
  const { translatedText: chatWithUs } = useTranslation('Chat with us');
  const { translatedText: frequentlyAskedQuestions } = useTranslation('Frequently Asked Questions');
  const { translatedText: questions } = useTranslation('questions');
  const { translatedText: noResultsFound } = useTranslation('No results found');
  const { translatedText: tryAdjustingSearch } = useTranslation('Try adjusting your search terms');
  const { translatedText: sendMessageTitle } = useTranslation('Send us a Message');
  const { translatedText: sendMessageSubtitle } = useTranslation('Have a question or need assistance? Fill out the form below and we\'ll get back to you within 24 hours.');
  const { translatedText: yourName } = useTranslation('Your Name');
  const { translatedText: namePlaceholder } = useTranslation('Enter your full name');
  const { translatedText: emailAddress } = useTranslation('Email Address');
  const { translatedText: emailPlaceholder } = useTranslation('your@email.com');
  const { translatedText: phoneNumber } = useTranslation('Phone Number');
  const { translatedText: phonePlaceholder } = useTranslation('+509 1234-5678');
  const { translatedText: subjectField } = useTranslation('Subject');
  const { translatedText: subjectPlaceholder } = useTranslation('Brief summary of your issue');
  const { translatedText: messageContent } = useTranslation('Message');
  const { translatedText: messagePlaceholder } = useTranslation('Describe your question or concern in detail...');
  const { translatedText: messageType } = useTranslation('Message Type');
  const { translatedText: selectMessageType } = useTranslation('Select message type');
  const { translatedText: preferredContactMethod } = useTranslation('Preferred Contact Method');
  const { translatedText: preferredContactTime } = useTranslation('Preferred Contact Time');
  const { translatedText: sending } = useTranslation('Sending...');
  const { translatedText: sendMessage } = useTranslation('Send Message');
  const { translatedText: messageSent } = useTranslation('Message Sent!');
  const { translatedText: messageSentDescription } = useTranslation('Thank you for reaching out. Our support team will review your message and get back to you within 24-48 hours.');
  const { translatedText: sendAnother } = useTranslation('Send Another Message');
  const { translatedText: required } = useTranslation('Required');
  const { translatedText: optional } = useTranslation('Optional');
  const { translatedText: referenceProvider } = useTranslation('Reference a Provider (Optional)');
  const { translatedText: searchProvider } = useTranslation('Search for a provider...');
  const { translatedText: noProviderFound } = useTranslation('No provider found');
  const { translatedText: selectedProviderLabel } = useTranslation('Selected Provider');
  const { translatedText: clearProvider } = useTranslation('Clear');
  const { translatedText: messageIdLabel } = useTranslation('Message ID');
  const { translatedText: trackYourMessage } = useTranslation('Save this ID to track your message');
  
  // Validation error messages
  const { translatedText: nameRequired } = useTranslation('Name is required');
  const { translatedText: nameMinLength } = useTranslation('Name must be at least 2 characters');
  const { translatedText: nameMaxLength } = useTranslation('Name cannot exceed 100 characters');
  const { translatedText: emailRequired } = useTranslation('Email is required');
  const { translatedText: emailInvalid } = useTranslation('Please enter a valid email address');
  const { translatedText: phoneInvalid } = useTranslation('Please enter a valid phone number');
  const { translatedText: subjectRequired } = useTranslation('Subject is required');
  const { translatedText: subjectMinLength } = useTranslation('Subject must be at least 3 characters');
  const { translatedText: subjectMaxLength } = useTranslation('Subject cannot exceed 200 characters');
  const { translatedText: contentRequired } = useTranslation('Message content is required');
  const { translatedText: contentMinLength } = useTranslation('Message must be at least 10 characters');
  const { translatedText: contentMaxLength } = useTranslation('Message cannot exceed 5000 characters');
  const { translatedText: submitErrorMessage } = useTranslation('Unable to send your message. Please try again later.');

  // Footer Translations
  const { translatedText: homeTitle } = useTranslation('Haitian Services Platform');
  const { translatedText: connectingHaitians } = useTranslation('Connecting Haitians Worldwide, One Service at a Time');
  const { translatedText: madeWithLove } = useTranslation('All rights reserved');
  const { translatedText: quickLinks } = useTranslation('Quick Links');
  const { translatedText: findProviders } = useTranslation('Find Providers');
  const { translatedText: supportUs } = useTranslation('Support Us');
  const { translatedText: helpAndSupport } = useTranslation('Help & Support');
  const { translatedText: aboutUs } = useTranslation('About Us');
  const { translatedText: forProviders } = useTranslation('For Providers');
  const { translatedText: becomeAProvider } = useTranslation('Become a Provider');
  const { translatedText: providerLogin } = useTranslation('Provider Login');
  const { translatedText: dashboard } = useTranslation('Dashboard');
  const { translatedText: settings } = useTranslation('Settings');
  const { translatedText: legal } = useTranslation('Legal');
  const { translatedText: termsOfService } = useTranslation('Terms of Service');
  const { translatedText: privacyPolicy } = useTranslation('Privacy Policy');
  const { translatedText: cookiePolicy } = useTranslation('Cookie Policy');

  // Translation-based options
  const { translatedText: generalInquiry } = useTranslation('General Inquiry');
  const { translatedText: serviceInquiry } = useTranslation('Service Inquiry');
  const { translatedText: bookingRequest } = useTranslation('Booking Request');
  const { translatedText: complaint } = useTranslation('Complaint');
  const { translatedText: feedback } = useTranslation('Feedback');
  const { translatedText: partnership } = useTranslation('Partnership');
  
  const { translatedText: morning } = useTranslation('Morning');
  const { translatedText: afternoon } = useTranslation('Afternoon');
  const { translatedText: evening } = useTranslation('Evening');
  const { translatedText: anytime } = useTranslation('Anytime');

  const { translatedText: generalInquiryCat } = useTranslation('General Inquiry');
  const { translatedText: serviceQuestion } = useTranslation('Service Question');
  const { translatedText: pricing } = useTranslation('Pricing');
  const { translatedText: availability } = useTranslation('Availability');
  const { translatedText: bookingIssue } = useTranslation('Booking Issue');
  const { translatedText: suggestion } = useTranslation('Suggestion');
  const { translatedText: other } = useTranslation('Other');

  // ======================================================
  // OPTIONS
  // ======================================================

  const messageTypeOptions = [
    { value: 'general', label: generalInquiry },
    { value: 'service_inquiry', label: serviceInquiry },
    { value: 'booking_request', label: bookingRequest },
    { value: 'complaint', label: complaint },
    { value: 'feedback', label: feedback },
    { value: 'partnership', label: partnership }
  ];

  const contactOptions = [
    { value: 'email', label: email },
    { value: 'phone', label: phoneNumber },
    { value: 'whatsapp', label: whatsapp }
  ];

  const timeOptions = [
    { value: 'morning', label: morning },
    { value: 'afternoon', label: afternoon },
    { value: 'evening', label: evening },
    { value: 'anytime', label: anytime }
  ];

  const categoryOptions = [
    { value: 'general_inquiry', label: generalInquiryCat },
    { value: 'service_question', label: serviceQuestion },
    { value: 'pricing', label: pricing },
    { value: 'availability', label: availability },
    { value: 'booking_issue', label: bookingIssue },
    { value: 'complaint', label: complaint },
    { value: 'suggestion', label: suggestion },
    { value: 'other', label: other }
  ];

  // ======================================================
  // CONTACT INFO FROM PLATFORM SETTINGS
  // ======================================================
  const contactInfo = {
    phone: publicSettings?.platformInfo?.contactPhone || '+509 1234-5678',
    email: publicSettings?.platformInfo?.contactEmail || 'support@haitianservices.com',
    whatsapp: publicSettings?.platformInfo?.contactPhone?.replace(/[^0-9]/g, '') || '50912345678'
  };

  // ======================================================
  // FAQ DATA
  // ======================================================

  const { translatedText: faq1Question } = useTranslation('How do I find a service provider?');
  const { translatedText: faq1Answer } = useTranslation('You can find service providers by browsing the "Find Providers" section. You can filter by service type, location, rating, and more. Simply tap on a provider to view their full profile and contact them.');
  const { translatedText: faq2Question } = useTranslation('Do I need to create an account to use the app?');
  const { translatedText: faq2Answer } = useTranslation('No, clients can browse services and view provider profiles without creating an account. However, providers need to create an account to list their services and manage their profile.');
  const { translatedText: faq3Question } = useTranslation('How do I contact a service provider?');
  const { translatedText: faq3Answer } = useTranslation('Each provider has a "Contact" button on their profile. You can call them directly, send a WhatsApp message, or email them. All contact information is available on their profile page.');
  const { translatedText: faq4Question } = useTranslation('What services are available on the platform?');
  const { translatedText: faq4Answer } = useTranslation('We offer a wide range of services including: Electricians, Plumbers, Masons, Painters, Carpenters, Barbers, HVAC Technicians, Interior Designers, Landscapers, Cleaners, Handyman services, and more.');
  const { translatedText: faq5Question } = useTranslation('How do I leave a review for a provider?');
  const { translatedText: faq5Answer } = useTranslation('After receiving service, you can leave a review on the provider\'s profile page. Tap the "Write a Review" button, rate your experience with stars, and share your feedback in the comment section.');
  const { translatedText: faq6Question } = useTranslation('Is the platform free to use?');
  const { translatedText: faq6Answer } = useTranslation('Yes, the Haitian Services platform is completely free for clients to browse and connect with providers. Providers can also list their services for free. We believe in supporting our community!');
  const { translatedText: faq7Question } = useTranslation('How do I report an issue or concern?');
  const { translatedText: faq7Answer } = useTranslation('If you encounter any issues, you can report them through the "Send us a Message" section below. Our team will review and address your concern within 24-48 hours.');
  const { translatedText: faq8Question } = useTranslation('Are providers verified?');
  const { translatedText: faq8Answer } = useTranslation('Yes, we verify providers through a thorough screening process. Verified providers have a checkmark badge on their profile, indicating they have met our quality and reliability standards.');

  const faqData = [
    { id: '1', question: faq1Question, answer: faq1Answer, icon: Search },
    { id: '2', question: faq2Question, answer: faq2Answer, icon: User },
    { id: '3', question: faq3Question, answer: faq3Answer, icon: Phone },
    { id: '4', question: faq4Question, answer: faq4Answer, icon: Wrench },
    { id: '5', question: faq5Question, answer: faq5Answer, icon: Star },
    { id: '6', question: faq6Question, answer: faq6Answer, icon: Heart },
    { id: '7', question: faq7Question, answer: faq7Answer, icon: Flag },
    { id: '8', question: faq8Question, answer: faq8Answer, icon: Shield },
  ];

  const filteredFaq = searchQuery
    ? faqData.filter(
        (item) =>
          item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.answer.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : faqData;

  const toggleSection = (id) => {
    setActiveSection(activeSection === id ? null : id);
  };

  // ======================================================
  // FORM HANDLING WITH REDUX
  // ======================================================

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = nameRequired;
    } else if (formData.name.length < 2) {
      errors.name = nameMinLength;
    } else if (formData.name.length > 100) {
      errors.name = nameMaxLength;
    }

    if (!formData.email.trim()) {
      errors.email = emailRequired;
    } else if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(formData.email)) {
      errors.email = emailInvalid;
    }

    if (formData.phone && !/^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/.test(formData.phone)) {
      errors.phone = phoneInvalid;
    }

    if (!formData.subject.trim()) {
      errors.subject = subjectRequired;
    } else if (formData.subject.length < 3) {
      errors.subject = subjectMinLength;
    } else if (formData.subject.length > 200) {
      errors.subject = subjectMaxLength;
    }

    if (!formData.content.trim()) {
      errors.content = contentRequired;
    } else if (formData.content.length < 10) {
      errors.content = contentMinLength;
    } else if (formData.content.length > 5000) {
      errors.content = contentMaxLength;
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      // Prepare message data for Redux
      const messageData = {
        ...formData,
        serviceProviderId: selectedProvider?.id || null,
        serviceProviderName: selectedProvider?.name || null,
        serviceType: selectedProvider?.serviceType || null,
        ipAddress: '192.168.1.1',
        userAgent: navigator.userAgent || 'Mozilla/5.0'
      };

      // Dispatch Redux action
      const result = await dispatch(createMessage(messageData)).unwrap();
      
      if (result?.data?.messageId) {
        setSubmittedMessageId(result.data.messageId);
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          content: '',
          messageType: 'general',
          preferredContact: 'email',
          preferredTime: 'anytime',
          serviceProviderId: '',
          serviceProviderName: '',
          serviceType: '',
          category: 'general_inquiry'
        });
        setSelectedProvider(null);
        setFormErrors({});
        setProviderSearchTerm('');
      }
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const handleContactMethod = (method, value) => {
    if (method === 'email') {
      window.location.href = `mailto:${value}`;
    } else if (method === 'phone') {
      window.location.href = `tel:${value.replace(/\s/g, '')}`;
    } else if (method === 'whatsapp') {
      window.open(`https://wa.me/${value.replace(/[^0-9]/g, '')}`, '_blank');
    }
  };

  // Provider search
  const filteredProviders = dummyProviders.filter(provider =>
    provider.name.toLowerCase().includes(providerSearchTerm.toLowerCase()) ||
    provider.serviceType.toLowerCase().includes(providerSearchTerm.toLowerCase())
  );

  const handleSelectProvider = (provider) => {
    setSelectedProvider(provider);
    setFormData(prev => ({
      ...prev,
      serviceProviderId: provider.id,
      serviceProviderName: provider.name,
      serviceType: provider.serviceType
    }));
    setShowProviderSearch(false);
    setProviderSearchTerm('');
  };

  const handleClearProvider = () => {
    setSelectedProvider(null);
    setFormData(prev => ({
      ...prev,
      serviceProviderId: '',
      serviceProviderName: '',
      serviceType: ''
    }));
  };

  const renderFAQItem = (item) => {
    const isActive = activeSection === item.id;
    const Icon = item.icon;
    const ChevronIcon = isActive ? ChevronUp : ChevronDown;

    return (
      <div
        key={item.id}
        className={`${styles.faqItem} ${isActive ? styles.active : ''}`}
        onClick={() => toggleSection(item.id)}
      >
        <div className={styles.faqItemBody}>
          <div className={styles.faqHeader}>
            <div className={styles.faqIcon}>
              <Icon size={20} color={colors.primary} />
            </div>
            <div className={styles.faqQuestion}>
              {item.question}
            </div>
            <ChevronIcon size={20} color={colors.textSecondary} className={styles.faqChevron} />
          </div>
          {isActive && (
            <div className={styles.faqAnswerContainer}>
              <p className={styles.faqAnswer}>{item.answer}</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ======================================================
  // RESET SUCCESS STATE WHEN CLOSING SUCCESS SCREEN
  // ======================================================
  const handleSendAnother = () => {
    dispatch(clearMessageSuccess());
    setSubmittedMessageId(null);
  };

  // ======================================================
  // RENDER
  // ======================================================
  return (
    <div className={styles.helpSupportContainer}>
      {/* Header */}
      <div className={styles.helpHeader}>
        <button 
          className={styles.backButton}
          onClick={() => navigate(-1)}
        >
          <ArrowLeft size={24} color={colors.white} />
        </button>
        <h5 className={styles.headerTitle}>{helpSupport}</h5>
        <div className={styles.headerRight} />
      </div>

      <div className={styles.container}>
        {/* Hero Section */}
        <div className={styles.heroSection} style={{ backgroundColor: colors.primary + '08' }}>
          <div className={styles.heroEmoji}>🙋</div>
          <h4 className={styles.heroTitle}>{howCanWeHelp}</h4>
          <p className={styles.heroSubtitle}>{heroSubtitle}</p>
        </div>

        {/* Search Bar */}
        <div className={styles.searchContainer}>
          <div className={styles.searchBar}>
            <Search size={20} color={colors.gray400} />
            <input
              type="text"
              className={styles.searchInput}
              placeholder={searchPlaceholder}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery.length > 0 && (
              <button 
                className={styles.clearSearch}
                onClick={() => setSearchQuery('')}
              >
                <X size={20} color={colors.gray400} />
              </button>
            )}
          </div>
        </div>

        {/* Quick Contact Options - Using platform settings */}
        <div className={styles.quickContactSection}>
          <h6 className={styles.sectionTitle}>{quickContact}</h6>
          <div className={styles.contactOptions}>
            <button 
              className={styles.contactOption}
              onClick={() => handleContactMethod('phone', contactInfo.phone)}
            >
              <div className={`${styles.contactIcon} ${styles.callIcon}`} style={{ backgroundColor: colors.primary }}>
                <Phone size={24} color={colors.white} />
              </div>
              <span className={styles.contactLabel}>{callUs}</span>
              <span className={styles.contactValue}>{contactInfo.phone}</span>
            </button>

            <button 
              className={styles.contactOption}
              onClick={() => handleContactMethod('whatsapp', contactInfo.whatsapp)}
            >
              <div className={`${styles.contactIcon} ${styles.whatsappIcon}`} style={{ backgroundColor: '#25D366' }}>
                <MessageCircle size={24} color={colors.white} />
              </div>
              <span className={styles.contactLabel}>{whatsapp}</span>
              <span className={styles.contactValue}>{chatWithUs}</span>
            </button>

            <button 
              className={styles.contactOption}
              onClick={() => handleContactMethod('email', contactInfo.email)}
            >
              <div className={`${styles.contactIcon} ${styles.emailIcon}`} style={{ backgroundColor: colors.secondary }}>
                <Mail size={24} color={colors.white} />
              </div>
              <span className={styles.contactLabel}>{email}</span>
              <span className={styles.contactValue}>{contactInfo.email}</span>
            </button>
          </div>
        </div>

        {/* FAQ Section */}
        <div className={styles.faqSection}>
          <div className={styles.sectionHeader}>
            <h6 className={styles.sectionTitle}>{frequentlyAskedQuestions}</h6>
            <span className={styles.sectionCount}>{filteredFaq.length} {questions}</span>
          </div>

          {filteredFaq.length > 0 ? (
            <div className={styles.faqList}>
              {filteredFaq.map(renderFAQItem)}
            </div>
          ) : (
            <div className={styles.noResults} style={{ backgroundColor: colors.gray50 }}>
              <HelpCircle size={60} color={colors.gray300} />
              <h6 className={styles.noResultsTitle}>{noResultsFound}</h6>
              <p className={styles.noResultsSubtitle}>{tryAdjustingSearch}</p>
            </div>
          )}
        </div>

        {/* Message Form Section */}
        <div className={styles.messageFormSection}>
          <div className={styles.messageFormHeader}>
            <FileText size={24} color={colors.primary} />
            <h6 className={styles.sectionTitle}>{sendMessageTitle}</h6>
          </div>
          <p className={styles.messageFormSubtitle}>{sendMessageSubtitle}</p>

          {submitSuccess ? (
            // Success State
            <div className={styles.successContainer}>
              <div className={styles.successIcon}>
                <CheckCircle size={64} color={colors.success || '#1A8C4A'} />
              </div>
              <h5 className={styles.successTitle}>{messageSent}</h5>
              <p className={styles.successMessage}>{messageSentDescription}</p>
              {submittedMessageId && (
                <div className={styles.successMessageId}>
                  <span className={styles.messageIdLabel}>{messageIdLabel}:</span>
                  <span className={styles.messageIdValue}>{submittedMessageId}</span>
                  <p className={styles.messageIdNote}>{trackYourMessage}</p>
                </div>
              )}
              <button
                className={styles.successButton}
                onClick={handleSendAnother}
              >
                {sendAnother}
              </button>
            </div>
          ) : (
            // Form
            <form className={styles.messageForm} onSubmit={handleSubmit}>
              {/* Name */}
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>
                  {yourName} <span className={styles.requiredStar}>*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  className={`${styles.formInput} ${formErrors.name ? styles.error : ''}`}
                  placeholder={namePlaceholder}
                  value={formData.name}
                  onChange={handleInputChange}
                  maxLength={100}
                  disabled={isSubmitting}
                />
                {formErrors.name && (
                  <span className={styles.errorMessage}>{formErrors.name}</span>
                )}
              </div>

              {/* Email */}
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>
                  {emailAddress} <span className={styles.requiredStar}>*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  className={`${styles.formInput} ${formErrors.email ? styles.error : ''}`}
                  placeholder={emailPlaceholder}
                  value={formData.email}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                />
                {formErrors.email && (
                  <span className={styles.errorMessage}>{formErrors.email}</span>
                )}
              </div>

              {/* Phone */}
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>
                  {phoneNumber} <span className={styles.optionalLabel}>({optional})</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  className={`${styles.formInput} ${formErrors.phone ? styles.error : ''}`}
                  placeholder={phonePlaceholder}
                  value={formData.phone}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                />
                {formErrors.phone && (
                  <span className={styles.errorMessage}>{formErrors.phone}</span>
                )}
              </div>

              {/* Subject */}
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>
                  {subjectField} <span className={styles.requiredStar}>*</span>
                </label>
                <input
                  type="text"
                  name="subject"
                  className={`${styles.formInput} ${formErrors.subject ? styles.error : ''}`}
                  placeholder={subjectPlaceholder}
                  value={formData.subject}
                  onChange={handleInputChange}
                  maxLength={200}
                  disabled={isSubmitting}
                />
                {formErrors.subject && (
                  <span className={styles.errorMessage}>{formErrors.subject}</span>
                )}
                <span className={styles.charCount}>{formData.subject.length}/200</span>
              </div>

              {/* Message Content */}
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>
                  {messageContent} <span className={styles.requiredStar}>*</span>
                </label>
                <textarea
                  name="content"
                  className={`${styles.formTextarea} ${formErrors.content ? styles.error : ''}`}
                  placeholder={messagePlaceholder}
                  value={formData.content}
                  onChange={handleInputChange}
                  rows={5}
                  maxLength={5000}
                  disabled={isSubmitting}
                />
                {formErrors.content && (
                  <span className={styles.errorMessage}>{formErrors.content}</span>
                )}
                <span className={styles.charCount}>{formData.content.length}/5000</span>
              </div>

              {/* Provider Reference */}
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>
                  {referenceProvider} <span className={styles.optionalLabel}>({optional})</span>
                </label>
                
                {selectedProvider ? (
                  <div className={styles.selectedProvider}>
                    <div className={styles.selectedProviderInfo}>
                      <Building2 size={18} color={colors.primary} />
                      <div>
                        <div className={styles.selectedProviderName}>{selectedProvider.name}</div>
                        <div className={styles.selectedProviderType}>{selectedProvider.serviceType}</div>
                      </div>
                    </div>
                    <button
                      type="button"
                      className={styles.clearProviderButton}
                      onClick={handleClearProvider}
                    >
                      <X size={16} />
                      <span>{clearProvider}</span>
                    </button>
                  </div>
                ) : (
                  <div className={styles.providerSearchContainer}>
                    <div 
                      className={styles.providerSearchToggle}
                      onClick={() => setShowProviderSearch(!showProviderSearch)}
                    >
                      <input
                        type="text"
                        className={styles.formInput}
                        placeholder={searchProvider}
                        value={providerSearchTerm}
                        onChange={(e) => {
                          setProviderSearchTerm(e.target.value);
                          if (!showProviderSearch) setShowProviderSearch(true);
                        }}
                        onFocus={() => setShowProviderSearch(true)}
                        disabled={isSubmitting}
                      />
                      <div className={styles.providerSearchIcon}>
                        <Search size={18} color={colors.gray400} />
                      </div>
                    </div>
                    
                    {showProviderSearch && (
                      <div className={styles.providerSearchResults}>
                        {filteredProviders.length > 0 ? (
                          filteredProviders.map(provider => (
                            <div
                              key={provider.id}
                              className={styles.providerSearchItem}
                              onClick={() => handleSelectProvider(provider)}
                            >
                              <div className={styles.providerItemName}>{provider.name}</div>
                              <div className={styles.providerItemType}>{provider.serviceType}</div>
                            </div>
                          ))
                        ) : (
                          <div className={styles.providerSearchEmpty}>
                            {noProviderFound}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Message Type */}
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>{messageType}</label>
                  <select
                    name="messageType"
                    className={styles.formSelect}
                    value={formData.messageType}
                    onChange={handleInputChange}
                    disabled={isSubmitting}
                  >
                    <option value="">{selectMessageType}</option>
                    {messageTypeOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>{preferredContactMethod}</label>
                  <select
                    name="preferredContact"
                    className={styles.formSelect}
                    value={formData.preferredContact}
                    onChange={handleInputChange}
                    disabled={isSubmitting}
                  >
                    {contactOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Preferred Time */}
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>{preferredContactTime}</label>
                <select
                  name="preferredTime"
                  className={styles.formSelect}
                  value={formData.preferredTime}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                >
                  {timeOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Submit Error */}
              {submitError && (
                <div className={styles.submitError}>
                  <AlertCircle size={18} color={colors.error || '#e74c3c'} />
                  <span>{submitError}</span>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                className={`${styles.submitButton} ${isSubmitting ? styles.disabled : ''}`}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={20} className={styles.spinnerBorder} />
                    <span>{sending}</span>
                  </>
                ) : (
                  <>
                    <Send size={20} color={colors.white} />
                    <span className={styles.submitButtonText}>{sendMessage}</span>
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </div>

      {/* Footer - Matching About Page */}
      <footer className={styles.footerFull}>
        <div className={styles.footerContent}>
          <div className={styles.footerGridFull}>
            <div className={styles.footerBrandColumn}>
              <div className={styles.footerBrandContainer}>
                <div className={styles.footerLogoWrapper}>
                  <img 
                    src="/logo/logo_hsm.png" 
                    alt="Haitian Services"
                    className={styles.footerLogo}
                  />
                </div>
                <div>
                  <div className={styles.footerBrandName}>{homeTitle}</div>
                  <div className={styles.footerBrandDescription}>{connectingHaitians}</div>
                </div>
              </div>
              <div className={styles.footerSocialLinks}>
                <a href="#" target="_blank" rel="noopener noreferrer" className={styles.footerSocialIcon}>
                  <img src="https://s.magecdn.com/social/tc-facebook.svg" alt="Facebook" width="20" height="20" />
                </a>
                <a href="#" target="_blank" rel="noopener noreferrer" className={styles.footerSocialIcon}>
                  <img src="https://s.magecdn.com/social/tc-instagram.svg" alt="Instagram" width="20" height="20" />
                </a>
                <a href="#" target="_blank" rel="noopener noreferrer" className={styles.footerSocialIcon}>
                  <img src="https://s.magecdn.com/social/tc-twitter.svg" alt="Twitter" width="20" height="20" />
                </a>
                <a href="#" target="_blank" rel="noopener noreferrer" className={styles.footerSocialIcon}>
                  <img src="https://s.magecdn.com/social/tc-youtube.svg" alt="YouTube" width="20" height="20" />
                </a>
              </div>
            </div>

            <div className={styles.footerLinksColumn}>
              <h3 className={styles.footerHeading}>{quickLinks}</h3>
              <ul className={styles.footerLinks}>
                <li><a href="/providers">{findProviders}</a></li>
                <li><a href="/donation">{supportUs}</a></li>
                <li><a href="/help-support">{helpAndSupport}</a></li>
                <li><a href="/about">{aboutUs}</a></li>
              </ul>
            </div>

            <div className={styles.footerLinksColumn}>
              <h3 className={styles.footerHeading}>{forProviders}</h3>
              <ul className={styles.footerLinks}>
                <li><a href="/register">{becomeAProvider}</a></li>
                <li><a href="/login">{providerLogin}</a></li>
                <li><a href="/provider-dashboard">{dashboard}</a></li>
                <li><a href="/provider-settings">{settings}</a></li>
              </ul>
            </div>

            <div className={styles.footerLinksColumn}>
              <h3 className={styles.footerHeading}>{legal}</h3>
              <ul className={styles.footerLinks}>
                <li><a href="/terms">{termsOfService}</a></li>
                <li><a href="/privacy">{privacyPolicy}</a></li>
                <li><a href="/cookies">{cookiePolicy}</a></li>
              </ul>
            </div>
          </div>

          <div className={styles.footerDividerFull}></div>
          <div className={styles.footerBottomFull}>
            <div className={styles.footerFlag}>
              <div className={styles.flagStripes}>
                <div className={`${styles.stripe} ${styles.stripeBlue}`}></div>
                <div className={`${styles.stripe} ${styles.stripeRed}`}></div>
              </div>
            </div>
            <div className={styles.footerBottomText}>
              © {new Date().getFullYear()} {homeTitle}, {madeWithLove}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HelpAndSupportPage;